import React from 'react';
import PropTypes from 'prop-types';

import {connect, dispatch} from 'utils/state_manager';

import {Box, Stack} from 'views/components';

import backend from 'utils/backend';

import StartStep from './new_steps/start_step/StartStep';
import ScheduleStep from './new_steps/schedule_step/ScheduleStep';
import CareplanStep from './new_steps/careplan_step/CareplanStep';
import CaregiverMatchingStep from './new_steps/caregiver_matching_step/CaregiverMatchingStep';
import StepProgressBar from './new_steps/StepProgressBar';

import {IntakeFormsSelf, IntakeFormsFamily} from './intake_forms';
import {FormSteps, FormKeysToFilter} from './utils/config';

import NavbarLogo from 'views/module/appbar/navbar/navbar_big_screen/navbar_top/NavbarLogo';

const ClientForm = (props) => {
  const clientForms = props.forms || [];
  const telemetryEnqueue = props.telemetryEnqueue;
  const clientStatus = props.clientStatus;
  const clientRn = props.clientRn;
  const progressWrap = props.statusProgressWrap;
  const clientDataForceReload = props.clientDataForceReload;

  const [activeStep, setActiveStep] = React.useState(FormSteps.START);
  const [forms, setForms] = React.useState([]);
  const [currentFormIndex, setCurrentFormIndex] = React.useState(0);

  const [progressStates, setProgressStates] = React.useState(() => ([
    'unopened',
    'unopened',
    'unopened',
    'unopened',
  ]));

  const currentForm = forms[currentFormIndex];

  const getProgressIndexForStep = React.useCallback((step) => {
    switch (step) {
      case FormSteps.START:
        return 0;
      case FormSteps.SCHEDULE:
        return 1;
      case FormSteps.CAREPLAN:
        return 2;
      case FormSteps.CAREGIVER_MATCHING:
        return 3;
      default:
        return 0;
    }
  }, []);

  const progressIndex = React.useMemo(() => getProgressIndexForStep(activeStep), [activeStep, getProgressIndexForStep]);

  React.useEffect(() => {
    telemetryEnqueue({
      name: 'client_form_progressed',
      properties: {
        activeStep,
        currentFormIndex,
        formTitle: currentForm?.title || null,
      },
    });
  }, [activeStep, currentFormIndex, currentForm, telemetryEnqueue]);

  React.useEffect(() => {
    const index = getProgressIndexForStep(activeStep);
    setProgressStates((prev) => {
      if (!prev[index] || prev[index] === 'unopened') {
        const next = [...prev];
        next[index] = 'opened';
        return next;
      }
      return prev;
    });
  }, [activeStep, getProgressIndexForStep]);

  React.useEffect(() => {
    // 1) Resolve which care-needs form exists and which intake set to use.
    const completedFormsByKey = new Map(clientForms.map((form) => [form.formKey, form]));
    const selfCareNeedsForm = completedFormsByKey.get('CLIENT_SELF_CARE_NEEDS');
    const familyCareNeedsForm = completedFormsByKey.get('CLIENT_FAMILY_CARE_NEEDS');
    const careNeedsForm = selfCareNeedsForm || familyCareNeedsForm || {};
    const intakeForms = selfCareNeedsForm ? IntakeFormsSelf : IntakeFormsFamily;
    const careNeedsAnswers = careNeedsForm?.answers || {};

    // 2) Filter intake forms based on "No" answers.
    const formKeysToExclude = new Set();
    Object.entries(FormKeysToFilter).forEach(([questionId, formKey]) => {
      const answer = careNeedsAnswers[questionId];
      if (!answer?.fieldType?.includes('radio')) {
        return;
      }
      const selectedValue = answer.value?.selectedOptionValue;
      if (selectedValue?.toLowerCase()?.includes('no')) {
        formKeysToExclude.add(formKey);
      }
    });

    const filteredForms = intakeForms.filter((form) => !formKeysToExclude.has(form.formKey));

    // 3) Hydrate forms with any completed snapshots/answers from the server.
    const normalizedForms = filteredForms.map((formDefinition) => {
      const completedForm = completedFormsByKey.get(formDefinition.formKey);
      if (!completedForm) {
        return formDefinition;
      }
      const snapshot = completedForm.formDefinitionSnapshot || {};
      return {
        ...formDefinition,
        ...snapshot,
        formKey: completedForm.formKey,
        title: snapshot.title || formDefinition.title,
        description: snapshot.description || formDefinition.description,
        formVersion: snapshot.formVersion ?? formDefinition.formVersion,
        questions: snapshot.questions || formDefinition.questions,
        answers: completedForm.answers || {},
        summary: completedForm.summary || null,
        updatedAt: completedForm.updatedAt,
        createdAt: completedForm.createdAt,
      };
    });

    setForms(normalizedForms);
  }, [clientForms]);

  const markProgressCompleted = React.useCallback((index) => {
    setProgressStates((prev) => {
      if (prev[index] === 'completed') {
        return prev;
      }
      const next = [...prev];
      next[index] = 'completed';
      return next;
    });
  }, []);

  const handleScheduleNext = React.useCallback(() => {
    markProgressCompleted(1);
    setActiveStep(FormSteps.CAREPLAN);
    window.scrollTo({top: 0, left: 0});
  }, [markProgressCompleted]);

  React.useEffect(() => {
    if (clientStatus === 'DETAILS_NEEDED') {
      setProgressStates(['completed', 'completed', 'unopened', 'unopened']);
      handleScheduleNext();
      return;
    }

    setCurrentFormIndex(0);
    setActiveStep(FormSteps.START);
    setProgressStates(['opened', 'unopened', 'unopened', 'unopened']);
  }, [clientForms, clientStatus, handleScheduleNext]);

  const handleStartNext = () => {
    markProgressCompleted(0);
    setActiveStep(FormSteps.SCHEDULE);
    window.scrollTo({top: 0, left: 0});
  };

  const handleCareplanNext = React.useCallback(() => {
    markProgressCompleted(2);
    setActiveStep(FormSteps.CAREGIVER_MATCHING);
    window.scrollTo({top: 0, left: 0});
  }, [markProgressCompleted]);

  const handleCaregiverMatchingNext = React.useCallback(() => {
    progressWrap(async () => {
      await backend.client.updateStatus(clientRn, {status: 'CG_SEARCH'});
      clientDataForceReload();
    });
  }, [clientDataForceReload, clientRn, progressWrap]);

  return (
    <Stack
      marginBottom={{xs: 8, sm: 20}}
    >
      <Stack spacing={{xs: 1, sm: 2}} marginBottom={{xs: 4, sm: 8}}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <NavbarLogo />
        </Box>
        <StepProgressBar
          activeIndex={progressIndex}
          stepStates={progressStates}
        />

      </Stack>
      <Box
        width={{md: 576, xs: '100%'}}
        maxWidth={{md: 576, xs: '100%'}}
        marginX='auto'
      >
        {
          activeStep === FormSteps.START &&
          <StartStep onNext={handleStartNext} />
        }
        {
          activeStep === FormSteps.SCHEDULE &&
          <ScheduleStep onNext={handleScheduleNext} />
        }
        {
          activeStep === FormSteps.CAREPLAN &&
          <CareplanStep onNext={handleCareplanNext} />
        }
        {
          activeStep === FormSteps.CAREGIVER_MATCHING &&
          <CaregiverMatchingStep onNext={handleCaregiverMatchingNext} />
        }
      </Box>
    </Stack>
  );
};

ClientForm.propTypes = {
  forms: PropTypes.array,
  clientStatus: PropTypes.string,
  clientRn: PropTypes.string,
  statusProgressWrap: PropTypes.func.isRequired,
  clientDataForceReload: PropTypes.func.isRequired,
  telemetryEnqueue: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  forms: state.clientData.forms,
  clientStatus: state.clientData?.clientDetail?.status,
  clientRn: state.user.clientRn,
});

export default connect(mapStateToProps, dispatch)(React.memo(ClientForm));
